import Navigation from "@/components/Navigation";
import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import ExpertSection from "@/components/ExpertSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { useEffect, useState } from "react";
import { Phone, Euro, Clock, Calendar, Mail, CheckCircle2, Shield, ExternalLink } from "lucide-react";
import cyberTerminalHero from "@/assets/cyber-terminal-hero.webp";
import { useImagePreload } from "@/hooks/useImagePreload";

const TerminBuchen = () => {
  useImagePreload(cyberTerminalHero);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    
    script.onload = () => {
      // Wait for iframe to load
      setTimeout(() => {
        const iframe = document.querySelector('.calendly-inline-widget iframe');
        if (iframe) {
          setIsLoading(false);
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      }, 2000);
    };
    
    script.onerror = () => {
      setHasError(true);
      setIsLoading(false);
    };
    
    document.head.appendChild(script);
    
    // Timeout fallback after 8 seconds
    const timeout = setTimeout(() => {
      if (isLoading) {
        setHasError(true);
        setIsLoading(false);
      }
    }, 8000);
    
    return () => {
      clearTimeout(timeout);
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);
  return <>
      <Navigation />
      <CookieBanner />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Termin buchen" }
      ]} />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="min-h-screen flex flex-col relative z-10">
      
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img src={cyberTerminalHero} alt="Jetzt beraten lassen" title="Kostenlose Cyberversicherungs-Beratung für Arztpraxen – Termin online buchen" className="w-full h-full object-cover opacity-20" loading="eager" decoding="async" />
          
        </div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10 animate-fade-in">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-6">
            <Phone className="w-7 h-7 text-accent" />
          </div>
          
          <h1 className="heading-1 mb-6 text-foreground leading-tight">Jetzt
beraten lassen</h1>
          
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-sm font-medium">
              Kostenlos • Unverbindlich • Persönlich
            </span>
          </div>
          
          <p className="body-text text-foreground/70 max-w-2xl mx-auto">
            Buchen Sie jetzt Ihren kostenlosen Beratungstermin für Ihre Cyberversicherung
          </p>
        </div>
      </section>

      {/* Calendly Section */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="relative">
            {/* Loading Skeleton */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg z-10 min-h-[900px]">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-accent/30 border-t-accent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-foreground/70">Kalender wird geladen...</p>
                </div>
              </div>
            )}
            
            {/* Error/Fallback State */}
            {hasError && (
              <div className="mb-6 p-8 bg-accent/10 border-2 border-accent/30 rounded-lg text-center">
                <Calendar className="w-12 h-12 text-accent mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Kalender konnte nicht geladen werden
                </h3>
                <p className="text-foreground/70 mb-4">
                  Kein Problem! Öffnen Sie den Kalender direkt auf Calendly:
                </p>
                <a
                  href="https://calendly.com/dewein/zoom-call-cyberversicherung"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-background font-semibold rounded-lg hover:bg-accent/90 transition-all"
                >
                  <Calendar className="w-5 h-5" />
                  Jetzt Termin buchen
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
            
            {/* Calendly Widget */}
            <div 
              className={`calendly-inline-widget w-full transition-opacity duration-300 ${
                isLoading ? 'opacity-0' : 'opacity-100'
              }`}
              data-url="https://calendly.com/dewein/zoom-call-cyberversicherung?background_color=000000&text_color=ffffff&primary_color=a2ff23"
              style={{
                minWidth: '320px',
                height: '900px'
              }}
            />
            
            {/* Backup-Button (always visible) */}
            <div className="mt-4 text-center">
              <p className="text-sm text-foreground/60 mb-2">
                Probleme mit dem Kalender?
              </p>
              <a
                href="https://calendly.com/dewein/zoom-call-cyberversicherung"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent hover:text-accent/80 text-sm font-medium transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Direkt zu Calendly
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-4">
              <Mail className="w-7 h-7 text-accent" />
            </div>
            <h2 className="heading-2 mb-3 text-foreground">
              Hinterlassen Sie uns Ihre Kontaktdaten
            </h2>
            <p className="body-text text-foreground/70 max-w-2xl mx-auto">
              Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-background/50 border border-accent/20 rounded-lg p-6 sm:p-8">
              <ContactForm />
            </div>

            {/* Info Box */}
            <div className="space-y-6">
              <div className="bg-card/30 border border-accent/20 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-foreground flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                  Ihre Vorteile
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                    <span className="text-foreground/80">
                      <strong className="text-foreground">Schnelle Antwort:</strong><br />
                      Wir melden uns innerhalb von 24 Stunden
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                    <span className="text-foreground/80">
                      <strong className="text-foreground">Kostenlos:</strong><br />
                      Unverbindliche Erstberatung
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                    <span className="text-foreground/80">
                      <strong className="text-foreground">Persönlich:</strong><br />
                      Individuelle Beratung für Ihre Praxis
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-accent rounded-full mt-2" />
                    <span className="text-foreground/80">
                      <strong className="text-foreground">Sicher:</strong><br />
                      Ihre Daten werden vertraulich behandelt
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-card/30 border border-accent/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-accent" />
                  <h4 className="font-semibold text-foreground">Datenschutz</h4>
                </div>
                <p className="text-sm text-foreground/70">
                  Ihre Kontaktdaten werden ausschließlich zur Beantwortung Ihrer Anfrage verwendet und nicht an Dritte weitergegeben.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature-Boxen mit harter Kante - moved to bottom */}
      <section className="py-12 border-y border-accent/20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center p-4 bg-background/50 rounded-lg border border-accent/20">
              <Clock className="w-8 h-8 mx-auto mb-2 text-accent" />
              <div className="text-2xl font-bold text-foreground mb-1">15</div>
              <div className="text-sm text-foreground/70">Min Beratung</div>
            </div>
            <div className="text-center p-4 bg-background/50 rounded-lg border border-accent/20">
              <Phone className="w-8 h-8 mx-auto mb-2 text-accent" />
              <div className="text-2xl font-bold text-foreground mb-1">Zoom</div>
              <div className="text-sm text-foreground/70">& Telefon</div>
            </div>
            <div className="text-center p-4 bg-background/50 rounded-lg border border-accent/20">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-accent" />
              <div className="text-2xl font-bold text-foreground mb-1">Sofort</div>
              <div className="text-sm text-foreground/70">Termin</div>
            </div>
          </div>
        </div>
      </section>

      <ExpertSection />

      <Footer />
      </div>
    </>;
};
export default TerminBuchen;